import {Alert} from '@mui/material';
import {DateTime} from 'luxon';
import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {SessionVideoSegment} from '../../../interfaces';
import {VideoTypes} from '../../../enums';

interface Props {
  sessionVideoSegments?: SessionVideoSegment[];
  sessionStartTime?: string;
  sessionEndTime?: string;
}

const SessionVideoSegmentNotOverlappingSessionWarning: React.FC<Props> = ({
  sessionVideoSegments,
  sessionStartTime,
  sessionEndTime,
}) => {
  const {t} = useTranslation();
  const showNotOverlappingWarning = useMemo(
    () =>
      (sessionVideoSegments ?? []).some(
        svs =>
          // make sure all the raw data requirements are met
          (svs.userSelectedStartTime || svs.startTime) &&
          svs.duration &&
          sessionStartTime &&
          sessionEndTime &&
          // make sure there is no overlap between the session video and the session start and end time
          // video end time is before session start time or video start time is after session end time
          (DateTime.fromISO(
            (svs.userSelectedStartTime ?? svs.startTime) as string,
          ).plus({seconds: svs.duration}) <
            DateTime.fromISO(sessionStartTime as string) ||
            DateTime.fromISO(svs.startTime as string).toJSDate() >
              DateTime.fromISO(sessionEndTime as string).toJSDate()),
      ),
    [sessionVideoSegments, sessionStartTime, sessionEndTime],
  );

  if (showNotOverlappingWarning) {
    return (
      <Alert severity="warning" variant="outlined" sx={{mb: 1}}>
        {(sessionVideoSegments ?? []).some(svs =>
          [VideoTypes.mp4, VideoTypes.mov, VideoTypes.pixellot].includes(
            svs.type as VideoTypes,
          ),
        )
          ? t('video.sessionVideoNotOverlappingSessionTimeAlignEvent')
          : t('video.sessionVideoNotOverlappingSessionTime')}
      </Alert>
    );
  }

  return null;
};

export default SessionVideoSegmentNotOverlappingSessionWarning;
